import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, Tabs, TabsList, TabsTrigger, TabsContent } from '@travellegacy/nexops-design-system';
import { MonthYearSelector } from '@travellegacy/nexops-design-system';
import { operationalMetricsAPI } from '@/shared/lib/api';
import { Loader2, Plane, PiggyBank, UserPlus, RefreshCcw, TrendingUp, DollarSign } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';

interface AirlineDistribution {
  airline: string;
  count: number;
}

interface IssuerDistribution {
  issuer: string;
  count: number;
}

interface OperationalMetrics {
  totalMilesUsed: number;
  totalEmissions: number;
  emissionsWithMiles: number;
  emissionsAgency: number;
  totalSavings: number;
  totalSales: number;
  totalProfit: number;
  airlineDistribution: AirlineDistribution[];
  issuerDistribution: IssuerDistribution[];
  newClientsCount: number;
  renewalsCount: number;
}

// Cores para cada companhia aérea
const AIRLINE_COLORS: Record<string, string> = {
  'Latam': '#8B5CF6', // Roxo
  'Gol': '#FF6600',   // Laranja Gol
  'Azul': '#60A5FA',  // Azul claro
  'Outros': '#94A3B8', // Cinza para outros
};

// Paleta de cores para emissores (cores vibrantes e distintas)
const ISSUER_COLORS = [
  '#8B5CF6', // Roxo
  '#10B981', // Verde
  '#F59E0B', // Amarelo
  '#EF4444', // Vermelho
  '#3B82F6', // Azul
  '#EC4899', // Rosa
  '#14B8A6', // Teal
  '#F97316', // Laranja escuro
  '#6366F1', // Indigo
  '#84CC16', // Lima
];

export default function OperationalDashboardPage() {
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());

  // Query para buscar métricas operacionais
  const { data: metricsResponse, isLoading } = useQuery({
    queryKey: ['operational-metrics', selectedMonth, selectedYear],
    queryFn: async () => {
      const response = await operationalMetricsAPI.getMetrics(selectedMonth, selectedYear);
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutos
  });

  const metrics = metricsResponse?.data as OperationalMetrics | undefined;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold">Dashboard Operacional</h1>
          <p className="text-muted-foreground mt-1">
            Acompanhe as métricas operacionais do mês
          </p>
        </div>

        <MonthYearSelector
          selectedMonth={selectedMonth}
          selectedYear={selectedYear}
          onMonthChange={setSelectedMonth}
          onYearChange={setSelectedYear}
        />
      </div>

      {/* Tabs */}
      <Tabs defaultValue="aereos" className="w-full">
        <TabsList className="grid w-full grid-cols-5 rounded-xl p-1">
          <TabsTrigger value="visao-geral" className="rounded-lg data-[state=active]:bg-blue-200 data-[state=active]:text-primary-foreground">Visão Geral</TabsTrigger>
          <TabsTrigger value="aereos" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Aéreos</TabsTrigger>
          <TabsTrigger value="hoteis" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Hotéis</TabsTrigger>
          <TabsTrigger value="seguros" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Seguros</TabsTrigger>
          <TabsTrigger value="outros" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Outros Serviços</TabsTrigger>
        </TabsList>

        {/* Aba: Visão Geral */}
        <TabsContent value="visao-geral" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Card: Novos Clientes */}
            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-950/30 dark:to-purple-900/20 border-purple-200 dark:border-purple-800">
              <CardHeader className="flex flex-row items-center justify-between space-y-0">
                <CardTitle className="text-sm font-medium text-purple-800 dark:text-purple-300">
                  Novos Clientes
                </CardTitle>
                <div className="p-2 bg-purple-200 dark:bg-purple-900/40 rounded-lg">
                  <UserPlus className="h-4 w-4 text-purple-700 dark:text-purple-300" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                  {metrics?.newClientsCount || 0}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Vendas aprovadas
                </p>
              </CardContent>
            </Card>

            {/* Card: Renovações */}
            <Card className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-950/30 dark:to-orange-900/20 border-orange-200 dark:border-orange-800">
              <CardHeader className="flex flex-row items-center justify-between space-y-0">
                <CardTitle className="text-sm font-medium text-orange-800 dark:text-orange-300">
                  Renovações
                </CardTitle>
                <div className="p-2 bg-orange-200 dark:bg-orange-900/40 rounded-lg">
                  <RefreshCcw className="h-4 w-4 text-orange-700 dark:text-orange-300" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                  {metrics?.renewalsCount || 0}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Vendas aprovadas
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Aba: Aéreos */}
        <TabsContent value="aereos" className="space-y-6">
          {/* Métricas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
        {/* Card: Operações */}
        <Card className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950/30 dark:to-slate-900/20 border-slate-200 dark:border-slate-800">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-sm font-medium text-slate-800 dark:text-slate-300">
              Operações
            </CardTitle>
            <div className="p-2 bg-slate-200 dark:bg-slate-900/40 rounded-lg">
              <TrendingUp className="h-4 w-4 text-slate-700 dark:text-slate-300" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-slate-600 dark:text-slate-400">
              {metrics?.totalEmissions || 0}
            </div>
            <p className="text-xs text-slate-600/70 dark:text-slate-400/70 mt-2">
              {metrics?.emissionsWithMiles || 0} com milhas • {metrics?.emissionsAgency || 0} agência
            </p>
          </CardContent>
        </Card>

        {/* Card: Milhas Utilizadas */}
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950/30 dark:to-blue-900/20 border-blue-200 dark:border-blue-800">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-sm font-medium text-blue-800 dark:text-blue-300">
              Milhas Utilizadas
            </CardTitle>
            <div className="p-2 bg-blue-200 dark:bg-blue-900/40 rounded-lg">
              <Plane className="h-4 w-4 text-blue-700 dark:text-blue-300" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {metrics?.totalMilesUsed.toLocaleString('pt-BR') || '0'}
            </div>
            {metrics && metrics.totalEmissions > 0 && (
              <p className="text-xs text-muted-foreground mt-2">
                {metrics.totalEmissions} {metrics.totalEmissions === 1 ? 'emissão' : 'emissões'}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Card: Economia Total */}
        <Card className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-950/30 dark:to-green-900/20 border-green-200 dark:border-green-800">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-sm font-medium text-green-800 dark:text-green-300">
              Economia Total
            </CardTitle>
            <div className="p-2 bg-green-200 dark:bg-green-900/40 rounded-lg">
              <PiggyBank className="h-4 w-4 text-green-700 dark:text-green-300" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {metrics?.totalSavings
                ? new Intl.NumberFormat('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  }).format(metrics.totalSavings)
                : 'R$ 0,00'}
            </div>
            {metrics && metrics.totalEmissions > 0 && (
              <p className="text-xs text-muted-foreground mt-2">
                Média de {new Intl.NumberFormat('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                }).format(metrics.totalSavings / metrics.totalEmissions)} por emissão
              </p>
            )}
          </CardContent>
        </Card>

        {/* Card: Vendas */}
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-950/30 dark:to-purple-900/20 border-purple-200 dark:border-purple-800">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-sm font-medium text-purple-800 dark:text-purple-300">
              Vendas
            </CardTitle>
            <div className="p-2 bg-purple-200 dark:bg-purple-900/40 rounded-lg">
              <DollarSign className="h-4 w-4 text-purple-700 dark:text-purple-300" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
              {metrics?.totalSales
                ? new Intl.NumberFormat('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  }).format(metrics.totalSales)
                : 'R$ 0,00'}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Total de vendas
            </p>
          </CardContent>
        </Card>

        {/* Card: Lucro */}
        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-950/30 dark:to-orange-900/20 border-orange-200 dark:border-orange-800">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-sm font-medium text-orange-800 dark:text-orange-300">
              Lucro
            </CardTitle>
            <div className="p-2 bg-orange-200 dark:bg-orange-900/40 rounded-lg">
              <TrendingUp className="h-4 w-4 text-orange-700 dark:text-orange-300" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
              {metrics?.totalProfit
                ? new Intl.NumberFormat('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  }).format(metrics.totalProfit)
                : 'R$ 0,00'}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Lucro líquido
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gráfico de Companhias Aéreas */}
        {metrics?.airlineDistribution && metrics.airlineDistribution.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Distribuição por Companhia Aérea</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={metrics.airlineDistribution}
                      dataKey="count"
                      nameKey="airline"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label={({ airline, count, percent }) =>
                        `${airline}: ${count} (${(percent * 100).toFixed(1)}%)`
                      }
                    >
                      {metrics.airlineDistribution.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={AIRLINE_COLORS[entry.airline] || '#94A3B8'}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value: number) => [`${value} emissões`, 'Total']}
                    />
                    <Legend
                      verticalAlign="bottom"
                      height={36}
                      formatter={(value: string) => {
                        const airlineData = metrics.airlineDistribution.find(item => item.airline === value);
                        return airlineData ? `${value} (${airlineData.count} emissões)` : value;
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Gráfico de Emissores */}
        {metrics?.issuerDistribution && metrics.issuerDistribution.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Distribuição por Emissor</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={metrics.issuerDistribution}
                      dataKey="count"
                      nameKey="issuer"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label={({ issuer, count, percent }) =>
                        `${issuer}: ${count} (${(percent * 100).toFixed(1)}%)`
                      }
                    >
                      {metrics.issuerDistribution.map((_entry, index) => (
                        <Cell
                          key={`cell-issuer-${index}`}
                          fill={ISSUER_COLORS[index % ISSUER_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value: number) => [`${value} emissões`, 'Total']}
                    />
                    <Legend
                      verticalAlign="bottom"
                      height={36}
                      formatter={(value: string) => {
                        const issuerData = metrics.issuerDistribution.find(item => item.issuer === value);
                        return issuerData ? `${value} (${issuerData.count} emissões)` : value;
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
        </TabsContent>

        {/* Aba: Hotéis */}
        <TabsContent value="hoteis" className="space-y-6">
          <div className="text-center py-12">
            <p className="text-muted-foreground">Conteúdo de Hotéis em breve...</p>
          </div>
        </TabsContent>

        {/* Aba: Seguros */}
        <TabsContent value="seguros" className="space-y-6">
          <div className="text-center py-12">
            <p className="text-muted-foreground">Conteúdo de Seguros em breve...</p>
          </div>
        </TabsContent>

        {/* Aba: Outros Serviços */}
        <TabsContent value="outros" className="space-y-6">
          <div className="text-center py-12">
            <p className="text-muted-foreground">Conteúdo de Outros Serviços em breve...</p>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
